import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { formatDisplayDateTime } from '@/utils/date';
import { formatCount } from '@/utils/number';
import { PCMP_TIMELINE_STATUS_COLOR } from '../../constants/pcmpProposal.constants';
import type { PcmpActivityCard, PcmpProposalStudentClass } from '../../types/pcmpProposal.types';
import { PcmpStatusPill } from './PcmpStatusPill';
import { PcmpTimelineEntryItem } from './PcmpTimelineEntryItem';

interface PcmpActivityHistoryProps {
  activity: PcmpActivityCard[];
  students: PcmpProposalStudentClass[];
}

/** Every recorded event, oldest first; same-user runs are grouped by the API. */
export function PcmpActivityHistory({ activity, students }: PcmpActivityHistoryProps) {
  const [isOpen, setIsOpen] = useState(false);
  const entryCount = activity.reduce((sum, card) => sum + card.entries.length, 0);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 rounded-lg bg-muted px-3 py-2 text-left text-sm font-medium">
        <span>
          Activity history{' '}
          <span className="font-normal text-muted-foreground">
            ({formatCount(entryCount, 'entry')})
          </span>
        </span>
        <ChevronDown className={cn('size-4 transition-transform', isOpen && 'rotate-180')} />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ol className="mt-3 flex flex-col gap-3">
          {activity.map((card) => (
            <li
              key={card.entries[0]?.eventId ?? card.occurredAt}
              className={cn(
                'flex flex-col gap-2 rounded-lg border p-3',
                card.isCurrentUser && 'border-primary',
              )}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold">{card.title}</span>
                {card.pill ? (
                  <PcmpStatusPill tone={PCMP_TIMELINE_STATUS_COLOR[card.status]}>
                    {card.pill}
                  </PcmpStatusPill>
                ) : null}
                {card.designationName ? (
                  <span className="text-xs text-muted-foreground">{card.designationName}</span>
                ) : null}
                <span className="ml-auto text-xs text-muted-foreground">
                  {formatDisplayDateTime(card.occurredAt)}
                </span>
              </div>
              <ul className="flex flex-col gap-2">
                {card.entries.map((entry) => (
                  <PcmpTimelineEntryItem key={entry.eventId} entry={entry} students={students} />
                ))}
              </ul>
            </li>
          ))}
        </ol>
      </CollapsibleContent>
    </Collapsible>
  );
}
